import {FC, MouseEvent, useMemo, useState} from 'react';

import TableRow from '@mui/material/TableRow';
import TableCell, {TableCellProps} from '@mui/material/TableCell';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import AdfScannerIcon from '@mui/icons-material/AdfScanner';

import {Box, CircularProgress, Collapse, IconButton, Stack, Tooltip, Typography, styled} from '@mui/material';

import {colors} from '../../../../../shared/colors';
import {PhotoForm} from './PhotoForm';
import {EditDelay} from '../EditDelay';
import {authState} from '../../../../../store/auth/atoms';
import {useRecoilState, useRecoilValue, useSetRecoilState} from 'recoil';
import {
  currentReasonDelay,
  currentServiceRequestState,
  delayDocLoading,
} from '../../../../../store/service-requests/atoms';
import generatePDF from 'react-to-pdf';
import {IServiceRequest} from '../../../../../shared/types/serviceRequest.types';
import {TypeAccountRoles} from '../../../../../shared/types/user-account.types';
import {getIsUserWorker} from '../../../../../global/lib';

const getTargetElement = (id: string) => document.getElementById(id);

const BorderlessTableCell = styled(TableCell)<TableCellProps>(({theme}) => ({
  '.MuiTableCell-root:not(.MuiTableCell-root + .MuiTableRow-root)': {
    borderBottom: 0,
  },
}));

interface IDelayRow {
  row: any;
  id: number;
}

export const DelayRow: FC<IDelayRow> = ({row, id}) => {
  const {user} = useRecoilValue(authState);
  const serviceRequest = useRecoilValue(currentServiceRequestState);
  const setCurrentDelay = useSetRecoilState(currentReasonDelay);
  const [docLoading, setDocLoading] = useRecoilState(delayDocLoading);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const isWorker = useMemo(() => {
    return getIsUserWorker(user);
  }, [user]);

  const isCustomer = user?.roles?.filter((i) => i !== TypeAccountRoles.CUSTOMER).length === 1;

  const toggleCollapse = (e: MouseEvent<HTMLButtonElement | HTMLTableRowElement>) => {
    e.stopPropagation();

    if (!row.comment) return;

    setOpen(!open);
  };

  const toggleEditDelay = (e: MouseEvent<HTMLButtonElement | HTMLTableRowElement>) => {
    e.stopPropagation();
    setOpenEdit(!open);
  };

  return (
    <>
      <TableRow hover selected={open}>
        <BorderlessTableCell sx={{width: '34px'}}>
          <IconButton aria-label="expand row" size="small" onClick={toggleCollapse} disabled={!row.comment}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </BorderlessTableCell>
        <BorderlessTableCell align="left">{id}</BorderlessTableCell>
        <BorderlessTableCell align="left" sx={{maxWidth: '16rem'}}>
          <Box sx={{maxWidth: '16rem', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block'}}>
            {new Intl.DateTimeFormat('ru-RU', {
              timeZone: 'Europe/Moscow',
              hour: 'numeric',
              minute: 'numeric',
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
            }).format(new Date(row.date))}
          </Box>
        </BorderlessTableCell>
        <BorderlessTableCell align="right">
          <Stack direction="row" spacing={1}>
            {!isCustomer &&
            !isWorker &&
            (serviceRequest?.statusCode === 'DELAYED' ||
              serviceRequest?.statusCode === 'EXECUTED' ||
              serviceRequest?.statusCode === 'IN_PROGRESS' ||
              serviceRequest?.statusCode === 'READY_TO_PROCESS') ? (
              <IconButton onClick={toggleEditDelay} size="small">
                <EditIcon />
              </IconButton>
            ) : null}
            {!isCustomer &&
              (serviceRequest?.statusCode === 'DELAYED' ||
                serviceRequest?.statusCode === 'EXECUTED' ||
                serviceRequest?.statusCode === 'IN_PROGRESS' ||
                serviceRequest?.statusCode === 'READY_TO_PROCESS') && (
                <Tooltip title="Печать акта недопуска" PopperProps={{disablePortal: true}}>
                  {loading ? (
                    <Box sx={{height: '33px', width: '33px'}}>
                      <CircularProgress sx={{height: '100%', width: '100%'}} color="primary" size="small" />
                    </Box>
                  ) : (
                    <IconButton
                      disabled={docLoading}
                      onClick={() => {
                        setLoading(true);
                        setDocLoading(true);
                        setCurrentDelay({delay: row, localOrder: id});
                        setTimeout(() => {
                          generatePDF(() => getTargetElement('delay-doc'), {
                            canvas: {
                              mimeType: 'image/jpeg',
                              qualityRatio: 0.1,
                            },
                          }).finally(() => {
                            setLoading(false);
                            setDocLoading(false);
                          });
                        }, 1000);
                      }}
                      size="small"
                    >
                      <AdfScannerIcon />
                    </IconButton>
                  )}
                </Tooltip>
              )}
          </Stack>
        </BorderlessTableCell>
        <BorderlessTableCell>
          <PhotoForm docs={[row.document, ...(row.document2 ? [row.document2] : [])]} />
        </BorderlessTableCell>
      </TableRow>
      {row.comment && (
        <TableRow sx={{backgroundColor: colors['bg--on-white']}}>
          <BorderlessTableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={5}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{margin: 1}}>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    margin: 1,
                  }}
                >
                  <Typography component="div" marginRight={2} variant="subtitle2" fontWeight={500}>
                    {'Комментарий: '}
                  </Typography>
                  <Typography
                    component="div"
                    sx={{wordBreak: 'break-all'}}
                    flexWrap="wrap"
                    variant="subtitle2"
                    fontWeight={400}
                  >
                    {row.comment}
                  </Typography>
                </Stack>
              </Box>
            </Collapse>
          </BorderlessTableCell>
        </TableRow>
      )}
      <EditDelay
        serviceRequest={serviceRequest as IServiceRequest}
        open={openEdit}
        handleClose={() => setOpenEdit(false)}
        delay={row}
      />
    </>
  );
};
